import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { IUserProps } from "../types/User";
import { IOrganizationProps } from "../types/Organization";
import { PerformRequest } from "../lib/usePerformRequest";
import { Endpoints } from "../lib/endpoints";
import { HttpCodes } from "../lib/data";

interface UserProfile {
  user: IUserProps;
  organization: IOrganizationProps;
  organizationId: number;
  organizationSlug: string;
}

interface UserContextType {
  userProfile: UserProfile | null;
  setUserProfile: (profile: UserProfile | null) => void;
  clearUserProfile: (organizationSlug?: string) => void;
  fetchUserProfile: (
    organizationSlug: string,
    organization: IOrganizationProps
  ) => Promise<void>;
  isAuthenticated: boolean;
  currentOrganizationId: number | null;
}

// Helper function to get token key from organization slug
export const getOrgTokenKey = (organizationSlug: string): string => {
  return `org_auth_${organizationSlug.replace(/-/g, "_")}`;
};

export const getOrgRefreshTokenKey = (organizationSlug: string): string => {
  return `org_refresh_${organizationSlug.replace(/-/g, "_")}`;
};

// Helper for organization-scoped user profile storage
export const getOrgUserProfileKey = (organizationSlug: string): string => {
  return `org_profile_${organizationSlug.replace(/-/g, "_")}`;
};

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const [userProfile, setUserProfileState] = useState<UserProfile | null>(null);

  const setUserProfile = (profile: UserProfile | null) => {
    setUserProfileState(profile);
    if (profile) {
      const profileKey = getOrgUserProfileKey(profile.organizationSlug);
      localStorage.setItem(profileKey, JSON.stringify(profile));
    } else if (userProfile?.organizationSlug) {
      const profileKey = getOrgUserProfileKey(userProfile.organizationSlug);
      localStorage.removeItem(profileKey);
    }
  };

  const fetchUserProfile = async (
    organizationSlug: string,
    organization: IOrganizationProps
  ) => {
    const tokenKey = getOrgTokenKey(organizationSlug);
    const token = localStorage.getItem(tokenKey);

    if (!token) {
      return;
    }

    try {
      const response = await PerformRequest<{
        data?: {
          user: IUserProps;
        };
      }>({
        method: Endpoints.GetAttendeeProfile().method,
        route: Endpoints.GetAttendeeProfile().route,
        doNotUseAuthorization: false, // Use the token from localStorage
        organizationSlug: organizationSlug,
      });
      console.log("Response", response);
      if (response.status === HttpCodes.Success) {
        const responseData = response.data as any;
        if (responseData?.data?.user) {
          const profile: UserProfile = {
            user: responseData.data.user,
            organization: organization,
            organizationId: organization.id,
            organizationSlug: organizationSlug,
          };
          setUserProfileState(profile);
          const profileKey = getOrgUserProfileKey(organizationSlug);
          localStorage.setItem(profileKey, JSON.stringify(profile));
        }
      }
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
      // If token is invalid, clear it
      localStorage.removeItem(tokenKey);
      localStorage.removeItem(getOrgRefreshTokenKey(organizationSlug));
    }
  };

  const clearUserProfile = (organizationSlug?: string) => {
    if (organizationSlug) {
      // Clear organization-specific tokens
      const tokenKey = getOrgTokenKey(organizationSlug);
      const refreshTokenKey = getOrgRefreshTokenKey(organizationSlug);
      const profileKey = getOrgUserProfileKey(organizationSlug);
      localStorage.removeItem(tokenKey);
      localStorage.removeItem(refreshTokenKey);
      localStorage.removeItem(profileKey);
    } else {
      // Clear all organization tokens (fallback)
      if (userProfile?.organizationSlug) {
        const tokenKey = getOrgTokenKey(userProfile.organizationSlug);
        const refreshTokenKey = getOrgRefreshTokenKey(
          userProfile.organizationSlug
        );
        const profileKey = getOrgUserProfileKey(userProfile.organizationSlug);
        localStorage.removeItem(tokenKey);
        localStorage.removeItem(refreshTokenKey);
        localStorage.removeItem(profileKey);
      }
    }
    setUserProfileState(null);
  };

  const isAuthenticated = userProfile !== null;
  const currentOrganizationId = userProfile?.organizationId || null;

  return (
    <UserContext.Provider
      value={{
        userProfile,
        setUserProfile,
        clearUserProfile,
        fetchUserProfile,
        isAuthenticated,
        currentOrganizationId,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}
